import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  PenTool,
  Eraser,
  Check,
  Loader2,
  Shield,
  FileCheck,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/apiClient';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface ElectronicSignatureProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: 'legal_document' | 'work_order' | 'quality_control' | 'delivery_note' | 'contract' | 'consent';
  documentId: string;
  documentTitle: string;
  documentDescription?: string;
  onSigned?: (signatureId: string, verificationCode: string) => void;
}

interface SignatureResult {
  signature_id: string;
  verification_code: string;
}

export function ElectronicSignature({
  open,
  onOpenChange,
  documentType,
  documentId,
  documentTitle,
  documentDescription,
  onSigned,
}: ElectronicSignatureProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [result, setResult] = useState<SignatureResult | null>(null);

  useEffect(() => {
    if (open && profile) {
      setSignerName(profile.full_name || user?.email || '');
      setHasSignature(false);
      setAgreedToTerms(false);
      setResult(null);
      initCanvas();
    }
  }, [open, profile]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Signature line
    ctx.strokeStyle = '#e5e5e5';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();

    // Pen settings
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    setHasSignature(false);
    initCanvas();
  };

  const getSignatureData = (): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
  };

  const signMutation = useMutation({
    mutationFn: async () => {
      const signatureData = getSignatureData();

      const { data, error } = await api.rpc('sign_document', {
        p_document_type: documentType,
        p_document_id: documentId,
        p_signer_id: user?.id,
        p_signer_name: signerName,
        p_signer_role: profile?.role || 'operator',
        p_signer_email: user?.email,
        p_signature_type: 'simple',
        p_signature_data: signatureData,
        p_metadata: {
          browser: navigator.userAgent,
          timestamp: new Date().toISOString(),
          document_title: documentTitle,
        },
      });

      if (error) throw error;

      // Get verification code
      const { data: sigData, error: sigError } = await api
        .from('electronic_signatures')
        .select('verification_code')
        .eq('id', data)
        .single();

      if (sigError) throw sigError;

      return {
        signature_id: data as string,
        verification_code: sigData.verification_code,
      };
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast({
        title: 'Documento firmado',
        description: 'La firma electronica se ha registrado correctamente',
      });
      onSigned?.(data.signature_id, data.verification_code);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al firmar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const copyVerificationCode = () => {
    if (result?.verification_code) {
      navigator.clipboard.writeText(result.verification_code);
      toast({
        title: 'Codigo copiado',
        description: 'El codigo de verificacion se ha copiado al portapapeles',
      });
    }
  };

  const canSign = hasSignature && agreedToTerms && signerName.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="h-5 w-5" />
            Firma Electronica
          </DialogTitle>
          <DialogDescription>
            Firme el documento de forma segura y legalmente vinculante
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Document Info */}
            <div className="p-3 bg-muted rounded-lg space-y-1">
              <div className="flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">{documentTitle}</span>
              </div>
              {documentDescription && (
                <p className="text-xs text-muted-foreground pl-6">
                  {documentDescription}
                </p>
              )}
              <div className="flex items-center gap-2 pl-6 text-xs text-muted-foreground">
                <Badge variant="outline" className="text-xs">
                  {documentType.replace('_', ' ')}
                </Badge>
                <span>{format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}</span>
              </div>
            </div>

            {/* Signer Name */}
            <div className="space-y-2">
              <Label htmlFor="signerName">Nombre del firmante</Label>
              <Input
                id="signerName"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Nombre completo"
              />
            </div>

            {/* Signature Canvas */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Dibuje su firma</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearCanvas}
                  className="h-7 text-xs"
                >
                  <Eraser className="h-3 w-3 mr-1" />
                  Borrar
                </Button>
              </div>
              <div className="border-2 border-dashed rounded-lg p-1">
                <canvas
                  ref={canvasRef}
                  className="w-full h-32 cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              {!hasSignature && (
                <p className="text-xs text-muted-foreground text-center">
                  Use el raton o el dedo para dibujar su firma
                </p>
              )}
            </div>

            <Separator />

            {/* Terms Agreement */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
              />
              <label htmlFor="terms" className="text-sm cursor-pointer leading-tight">
                Acepto que esta firma electronica tiene la misma validez legal que una firma manuscrita,
                conforme al Reglamento eIDAS y la normativa aplicable.
              </label>
            </div>

            {/* Security Notice */}
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Esta firma sera registrada con marca de tiempo, hash criptografico y codigo de
                verificacion unico para garantizar su autenticidad e integridad.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          /* Success State */
          <div className="space-y-4 py-4">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Documento Firmado</h3>
              <p className="text-sm text-muted-foreground">
                La firma se ha registrado correctamente
              </p>
            </div>

            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Codigo de Verificacion</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyVerificationCode}
                  className="h-7"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copiar
                </Button>
              </div>
              <div className="font-mono text-xl text-center tracking-widest font-bold">
                {result.verification_code}
              </div>
              <p className="text-xs text-center text-muted-foreground">
                Guarde este codigo para verificar la autenticidad de la firma
              </p>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Firmante:</strong> {signerName}</p>
              <p><strong>Fecha:</strong> {format(new Date(), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}</p>
              <p><strong>Documento:</strong> {documentTitle}</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => signMutation.mutate()}
                disabled={!canSign || signMutation.isPending}
              >
                {signMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Firmando...
                  </>
                ) : (
                  <>
                    <PenTool className="h-4 w-4 mr-2" />
                    Firmar Documento
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Verification Component
interface SignatureVerificationProps {
  verificationCode?: string;
}

export function SignatureVerification({ verificationCode: initialCode }: SignatureVerificationProps) {
  const { toast } = useToast();
  const [code, setCode] = useState(initialCode || '');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    if (!code.trim()) {
      toast({
        title: 'Codigo requerido',
        description: 'Ingrese el codigo de verificacion',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await api.rpc('verify_signature', {
        p_verification_code: code.trim().toUpperCase(),
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setResult(data[0]);
      } else {
        setResult({ not_found: true });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Codigo de verificacion"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="font-mono"
        />
        <Button onClick={verify} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verificar'}
        </Button>
      </div>

      {result && (
        <div className={cn(
          "p-4 rounded-lg border",
          result.not_found ? "bg-red-50 border-red-200" :
          result.is_valid ? "bg-green-50 border-green-200" :
          "bg-orange-50 border-orange-200"
        )}>
          {result.not_found ? (
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              <span>Firma no encontrada</span>
            </div>
          ) : result.is_valid ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-700">
                <Check className="h-5 w-5" />
                <span className="font-medium">Firma Valida</span>
              </div>
              <div className="text-sm space-y-1">
                <p><strong>Firmante:</strong> {result.signer_name}</p>
                <p><strong>Rol:</strong> {result.signer_role}</p>
                <p><strong>Fecha:</strong> {format(new Date(result.signed_at), "dd/MM/yyyy HH:mm", { locale: es })}</p>
                <p><strong>Tipo:</strong> {result.document_type}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Firma Revocada</span>
              </div>
              <p className="text-sm">{result.revocation_reason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
